<template>
  <div class="appointments-section mt-2">
    <p class="appointments-section-title px-2">Appointments</p>
    <div class="appointments-card">
      <div v-for="item in appointmentItems" :key="item.id" class="appointment-row">
        <div class="appointment-icon">
          <v-icon size="18" color="deep-orange-darken-1">mdi-calendar-outline</v-icon>
        </div>

        <div class="appointment-copy">
          <p class="appointment-no">{{ item.appointmentNo }}</p>
          <p class="appointment-meta">{{ item.scheduleLabel }}</p>
        </div>

        <div class="appointment-status">
          <span class="status-dot" :class="`status-dot--${item.statusKey}`" />
          <span class="status-label">{{ item.statusLabel }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

type SubscriptionDetailResponse = {
  appointments?: {
    id: string | number;
    appointment_no: string | null;
    appointment_date: string | null;
    appointment_time: string | null;
    status: string | null;
  }[];
};

const props = defineProps<{
  detail: SubscriptionDetailResponse;
}>();

function formatDateTime(dateValue: string | null, timeValue: string | null) {
  if (!dateValue) return "-";
  const date = new Date(`${dateValue}T${timeValue || "00:00:00"}`);
  if (Number.isNaN(date.getTime())) return "-";
  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${datePart} · ${timePart}`;
}

function statusMeta(status: string | null | undefined) {
  const key = String(status ?? "").trim().toLowerCase();
  switch (key) {
    case "booked":
      return { key: "booked", label: "Booked" };
    case "scheduled":
      return { key: "scheduled", label: "Scheduled" };
    case "completed":
      return { key: "completed", label: "Completed" };
    case "cancelled":
    case "canceled":
      return { key: "cancelled", label: "Cancelled" };
    default:
      return { key: "scheduled", label: "Scheduled" };
  }
}

const appointmentItems = computed(() =>
  (props.detail.appointments ?? []).map((item) => {
    const meta = statusMeta(item.status);
    return {
      id: item.id,
      appointmentNo: item.appointment_no || "-",
      scheduleLabel: formatDateTime(item.appointment_date, item.appointment_time),
      statusKey: meta.key,
      statusLabel: meta.label,
    };
  }),
);
</script>

<style scoped>
.appointments-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.appointments-section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #6b7280;
}

.appointments-card {
  padding: 0;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
}

.appointment-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-bottom: 1px solid #e5e7eb;
}

.appointment-row:last-child {
  border-bottom: 0;
}

.appointment-icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(249, 115, 22, 0.12);
  flex: 0 0 auto;
}

.appointment-copy {
  min-width: 0;
  flex: 1;
}

.appointment-no {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.appointment-meta {
  margin: 2px 0 0;
  font-size: 14px;
  color: #6b7280;
}

.appointment-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #3b82f6;
}

.status-dot--scheduled {
  background: #f59e0b;
}

.status-dot--completed {
  background: #22c55e;
}

.status-dot--booked {
  background: #3b82f6;
}

.status-dot--cancelled {
  background: #ef4444;
}

.status-label {
  font-size: 13px;
  font-weight: 400;
  color: #475569;
}
</style>
